import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Lock, MonitorUp, ArrowDown } from "lucide-react";
import JigglerTool from "@/components/JigglerTool";
import { PlatformLogos } from "@/components/PlatformLogos";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { scrollToId } from "@/lib/ui";
import { getStats } from "@/lib/api";

export default function Hero() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    getStats().then(setStats).catch(() => setStats(null));
  }, []);

  return (
    <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(55% 45% at 72% 22%, rgba(0,255,102,0.09), transparent 70%)" }}
      />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full relative grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <div className="min-w-0 lg:col-span-7">
          <h1 className="font-display font-black tracking-normal leading-[1.02] text-[32px] min-[380px]:text-[40px] sm:text-6xl xl:text-[78px] text-white" data-testid="hero-title">
            <MaskedLine delay={0.1}>KEEP YOUR</MaskedLine>
            <MaskedLine delay={0.24}>STATUS</MaskedLine>
            <MaskedLine delay={0.38}>
              <span>
                ACTIVE<span className="text-neon drop-shadow-[0_0_18px_rgba(0,255,102,0.8)]">.</span>
              </span>
            </MaskedLine>
          </h1>

          <PlatformLogos />

          <FadeUp delay={0.55}>
            <p className="mt-8 text-mist text-base sm:text-lg leading-relaxed max-w-xl">
              The only browser-based mouse jiggler that keeps your screen awake and Microsoft Teams green —
              without installing any software. No downloads. No admin rights. No traces.
            </p>
          </FadeUp>

          <FadeUp delay={0.68}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button
                onClick={() => (window.location.pathname === "/" ? scrollToId("#tool") : navigate("/"))}
                data-testid="hero-start-button"
                className="inline-flex items-center gap-2.5 rounded-full bg-neon text-ink font-bold px-8 py-4 transition-all duration-200 hover:-translate-y-0.5 hover:bg-neonhover hover:shadow-[0_0_40px_rgba(0,255,102,0.5)]"
              >
                Start a free session
              </button>
              <button
                onClick={() => navigate("/how-it-works")}
                data-testid="hero-how-button"
                className="inline-flex items-center gap-2.5 rounded-full border border-white/15 text-white font-semibold px-8 py-4 transition-colors hover:border-neon/60 hover:text-neon"
              >
                How it works
                <ArrowDown size={16} />
              </button>
            </div>
          </FadeUp>

          <FadeUp delay={0.8}>
            <div className="mt-10 flex flex-wrap gap-x-7 gap-y-3" data-testid="hero-trust-chips">
              {[
                { icon: MonitorUp, label: "Zero installation" },
                { icon: Lock, label: "No admin rights" },
                { icon: ShieldCheck, label: "100% safe" },
              ].map((c) => (
                <span key={c.label} className="flex items-center gap-2 text-sm text-mist">
                  <c.icon size={15} className="text-neon" />
                  {c.label}
                </span>
              ))}
            </div>
          </FadeUp>

          {stats && stats.total_minutes > 0 && (
            <FadeUp delay={0.9}>
              <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.22em] text-dim" data-testid="hero-stats-counter">
                <span className="text-neon">
                  {stats.total_minutes < 120
                    ? `${Math.round(stats.total_minutes).toLocaleString("en-US")} minutes`
                    : `${Math.round(stats.total_minutes / 60).toLocaleString("en-US")} hours`}
                </span>{" "}
                of green status kept alive across{" "}
                <span className="text-white">{stats.total_sessions.toLocaleString("en-US")} sessions</span>
              </p>
            </FadeUp>
          )}
        </div>

        <div className="min-w-0 lg:col-span-5 relative">
          <FadeUp delay={0.45} y={40}>
            <JigglerTool />
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
